import React, { useCallback, useEffect, useMemo, useState } from "react";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import Main from "./Main";
import {
  AppBreadcrumbs,
  CompactSelect,
  ListingFilters,
  ListingPagination,
  StatusBadge,
  useListingState,
  TXN_STATUS_OPTIONS,
} from "../components/listing";
import { useNotify } from "../context/NotifyContext";
import { autoDateFormat } from "../utils/autoDateFormate";
import {
  getTransactionReport,
  exportTransactionReport,
} from "../api/dataTransferApi";

const DEFAULT_SERVICES = [
  { key: "ALL", label: "All Services" },
  { key: "ADD_MONEY", label: "Add Money" },
  { key: "RECHARGE", label: "Utility (recharge / bill / Fastag / LPG)" },
  { key: "SETTLEMENT", label: "Settlement" },
  { key: "DMT", label: "DMT" },
  { key: "AEPS", label: "AEPS" },
];

const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const formatINR = (amount) => {
  const num = Number(amount) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(num);
};

const DataTransfer = () => {
  const { notify } = useNotify();
  const [service, setService] = useState("ALL");
  const [services, setServices] = useState(DEFAULT_SERVICES);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const listing = useListingState({
    syncUrl: false,
    startDate: daysAgoISO(7),
    endDate: todayISO(),
    limit: 25,
  });

  const reportParams = useMemo(
    () => ({
      service,
      startDate: listing.startDate,
      endDate: listing.endDate,
      status: listing.status || undefined,
      search: listing.debouncedSearch || undefined,
      page: listing.page,
      limit: listing.limit,
    }),
    [
      service,
      listing.startDate,
      listing.endDate,
      listing.status,
      listing.debouncedSearch,
      listing.page,
      listing.limit,
    ]
  );

  const loadReport = useCallback(async () => {
    setLoading(true);
    const res = await getTransactionReport(reportParams);
    if (res.success) {
      setRows(res.data?.rows || []);
      setSummary(res.data?.summary || null);
      setPagination(
        res.data?.pagination || {
          page: 1,
          limit: 25,
          total: 0,
          totalPages: 1,
        }
      );
      if (res.data?.services?.length) setServices(res.data.services);
    } else {
      setRows([]);
      setSummary(null);
      notify(res.message || "Failed to load report", "error");
    }
    setLoading(false);
  }, [notify, reportParams]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const exportReport = async () => {
    setBusy(true);
    const res = await exportTransactionReport(reportParams);
    if (res.success) notify("Report downloaded", "success");
    else notify(res.message || "Export failed", "error");
    setBusy(false);
  };

  return (
    <Main showRightPanel={false}>
      <div className="mt-1">
        <AppBreadcrumbs
          items={[
            { label: "Dashboard", to: "/dashboard" },
            { label: "Reports / Export" },
          ]}
        />

        <div className="ds-card mt-2 mb-2">
          <h2 className="text-xs font-semibold text-[var(--text-dark)] m-0">
            Transaction Reports & Export
          </h2>
          <p className="text-[12px] text-[var(--text-muted)] m-0 mt-1">
            Filter your transactions by service, date and status, then
            download the Excel report.
          </p>
        </div>

        <div className="ds-card text-sm">
          <ListingFilters
            onClear={listing.clearFilters}
            startDate={listing.startDate}
            endDate={listing.endDate}
            onStartDate={listing.setStartDate}
            onEndDate={listing.setEndDate}
            search={listing.search}
            onSearch={listing.setSearch}
            searchPlaceholder="Search Txn ID"
            status={listing.status}
            onStatus={listing.setStatus}
            statusOptions={TXN_STATUS_OPTIONS}
            actions={
              <>
                <label className="ds-listing-field">
                  <span className="ds-listing-field__label">Service</span>
                  <CompactSelect
                    className="ds-filter-status-select"
                    value={service}
                    onChange={(v) => {
                      setService(v);
                      listing.setPage(1);
                    }}
                    options={services.map((s) => ({
                      value: s.key,
                      label: s.label,
                    }))}
                  />
                </label>
                <button
                  type="button"
                  className="ds-action-btn"
                  onClick={loadReport}
                  disabled={loading}
                >
                  <AssessmentOutlinedIcon fontSize="small" />{" "}
                  {loading ? "LOADING…" : "LOAD REPORT"}
                </button>
              </>
            }
            onExport={exportReport}
            exporting={busy}
          />

          {summary ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 mb-3">
              {[
                ["Total Txns", summary.total],
                ["Success", summary.successCount],
                ["Failed", summary.failedCount],
                ["Pending", summary.pendingCount],
                ["Total Amount", formatINR(summary.totalAmount)],
                ["Success Amount", formatINR(summary.successAmount)],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="border border-[var(--border-color)] rounded-[3px] p-2 bg-[var(--bg-app)]"
                >
                  <p className="m-0 text-[11px] text-[var(--text-muted)]">
                    {label}
                  </p>
                  <p className="m-0 mt-1 text-sm font-semibold text-[var(--text-dark)]">
                    {value}
                  </p>
                </div>
              ))}
            </div>
          ) : null}

          {summary?.byService?.length ? (
            <div className="ds-table-wrap ds-scroll mb-3">
              <table className="ds-table ds-table-listing min-w-[720px]">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Count</th>
                    <th>Success</th>
                    <th>Failed</th>
                    <th>Pending</th>
                    <th>Amount</th>
                    <th>Success Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byService.map((s) => (
                    <tr key={s.service}>
                      <td className="font-semibold">{s.service}</td>
                      <td>{s.count}</td>
                      <td>{s.successCount}</td>
                      <td>{s.failedCount}</td>
                      <td>{s.pendingCount}</td>
                      <td>{formatINR(s.amount)}</td>
                      <td>{formatINR(s.successAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {summary?.byDay?.length ? (
            <div className="ds-table-wrap ds-scroll mb-3">
              <table className="ds-table ds-table-listing min-w-[520px]">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Count</th>
                    <th>Amount</th>
                    <th>Success Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byDay.map((d) => (
                    <tr key={d.date}>
                      <td>{d.date}</td>
                      <td>{d.count}</td>
                      <td>{formatINR(d.amount)}</td>
                      <td>{formatINR(d.successAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <div className="ds-table-wrap ds-scroll">
            <table className="ds-table ds-table-listing min-w-[880px]">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Txn ID</th>
                  <th>Service</th>
                  <th>Amount</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Mode</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center text-[var(--text-muted)]"
                    >
                      {loading
                        ? "LOADING REPORT…"
                        : "NO TRANSACTIONS IN SELECTED RANGE"}
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => (
                    <tr key={`${row.txnId}-${index}`}>
                      <td>
                        {(pagination.page - 1) * pagination.limit + index + 1}
                      </td>
                      <td>{autoDateFormat(row.date) || "—"}</td>
                      <td className="font-semibold">{row.txnId || "—"}</td>
                      <td>{row.service || "—"}</td>
                      <td>{formatINR(row.amount)}</td>
                      <td>{row.type || "—"}</td>
                      <td>
                        <StatusBadge status={row.status} />
                      </td>
                      <td>{row.mode || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <ListingPagination
            page={listing.page}
            limit={listing.limit}
            total={pagination.total}
            totalPages={pagination.totalPages}
            onPageChange={listing.setPage}
            onLimit={listing.setLimit}
            limitOptions={[25, 50, 100]}
          />
        </div>
      </div>
    </Main>
  );
};

export default DataTransfer;
